import { Box, Button, Grid, Menu, MenuItem } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import Iconify from '../../../../reusable-components/iconify';
import { HOST_API_URL } from '../../../../config-global';
import NewItemDialog from './NewItemDialog';
import { ScopeGuard, useScopeCheck } from '../../../../reusable-components/scopes';
import { ConfirmDialog } from '../../../../reusable-components/confirm-dialog';
import DropdownAction from '../../../../reusable-components/dropdown-action/DropdownAction';

CustomToolbar.propTypes = {
  setViewAllSKUs: PropTypes.func,
  viewAllSKUs: PropTypes.bool,
  selectMode: PropTypes.bool,
  onToggleSelectMode: PropTypes.func,
  selectedRows: PropTypes.array,
  onCancelSelect: PropTypes.func,
  onConfirmDelete: PropTypes.func
};

export default function CustomToolbar({
  viewAllSKUs,
  setViewAllSKUs,
  selectMode,
  onToggleSelectMode,
  selectedRows = [],
  onCancelSelect,
  onConfirmDelete
})
{
  const selectedCount = selectedRows.length;
  const hasCatalogEditAccess = useScopeCheck(['Item-Catalog-Edit'], true);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const downloadCatalog = async () => {
    //currently this is not working on dev environment as HOST_API_URL is not set but it will work on production
    window.open(`${HOST_API_URL}Api/DownloadCatalog`, '_blank');
  };

  const handleDownload = () => {
    handleClose();
    downloadCatalog();
  };

  const handleDeleteMultiple = () => {
    handleClose();
    onToggleSelectMode();
  };

  return (
    <>
      <Grid container direction="row" justifyContent="flex-start" alignItems="flex-end">
        <Box sx={{ display: 'inline-block', mr: 1 }}>
          <NewItemDialog />
        </Box>
        <Box sx={{ display: 'inline-block' }}>
          <Button
            variant={viewAllSKUs ? "contained" : "outlined"}
            sx={{ mr: 1 }}
            size="small"
            color="secondary"
            onClick={() => {
              setViewAllSKUs(viewState => !viewState);
              const currentUrl = new URL(window.location.href);
              if (viewAllSKUs) currentUrl.searchParams.delete("viewallskus")
              else currentUrl.searchParams.set("viewallskus", "1");
              window.history.replaceState(null, document.title, currentUrl);
            }}
            startIcon={<Iconify icon="eva:search-fill" />}
          >
            View All {viewAllSKUs ? "Products" : "SKUs"}
          </Button>
        </Box>
        {!selectMode && (
          <Box sx={{ display: 'inline-block' }}>
            <Button
              sx={{
                mr: 1,
                minWidth: 0,
                px: 1,
                '&::after': { content: '"\\200B"' },
              }}
              variant="outlined"
              size="small"
              color="secondary"
              onClick={handleClick}
            >
              <Iconify icon="mdi:more-vert" />
            </Button>

            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              slotProps={{
                paper: {
                  sx: {
                    width: 210,
                    p: 1,
                    borderRadius: '5px',
                    boxShadow: '0px 2px 6px rgba(0,0,0,.06), 0px 8px 24px rgba(0,0,0,.12)',
                    '& .MuiMenu-list': {
                      p: 0,
                      display: 'flex',
                      flexDirection: 'column',
                    },
                  },
                },
              }}
            >
              <MenuItem
                disableRipple
                disableGutters
                sx={{ p: 0, minHeight: 'unset', '&:hover': { bgcolor: 'transparent' } }}
              >
                <DropdownAction badgeIcon="mdi:download-outline" onClick={handleDownload}>
                  Download
                </DropdownAction>
              </MenuItem>

              {onToggleSelectMode && hasCatalogEditAccess && (
                <MenuItem
                  disableRipple
                  disableGutters
                  sx={{ p: 0, minHeight: 'unset', '&:hover': { bgcolor: 'transparent' } }}
                >
                  <DropdownAction
                    badgeIcon="mdi:delete-outline"
                    iconColor="error.main"
                    iconBgColor="#FDEDED"
                    textColor="error.main"
                    onClick={handleDeleteMultiple}
                    disabled={selectedCount > 0}
                  >
                    Delete Multiple Items
                  </DropdownAction>
                </MenuItem>
              )}
            </Menu>
          </Box>
        )}
        {onToggleSelectMode && selectMode && (
          <ScopeGuard scopes={['Item-Catalog-Edit']} allowAdmin>
            <>
              <Box sx={{ display: 'inline-block' }}>
                <ConfirmDialog
                  button="outlined"
                  icon="delete-outline"
                  title="Delete Products"
                  color="error"
                  content={
                    <>
                      Are you sure you want to delete {selectedCount} product{selectedCount === 1 ? '' : 's'}?
                      <Box component="ul" sx={{ pl: 3, mt: 1, maxHeight: 300, overflowY: 'auto' }}>
                        {selectedRows.map(({ productId, description }) => (
                          <li key={productId}>{description}</li>
                        ))}
                      </Box>
                    </>
                  }
                  actionButton="Delete"
                  buttonStyles={{ mr: 1 }}
                  disabled={!selectedCount}
                  action={onConfirmDelete}
                >
                  Delete {selectedCount} Item{selectedCount === 1 ? '' : 's'}
                </ConfirmDialog>
              </Box>
              <Box sx={{ display: 'inline-block' }}>
                <Button variant="outlined" sx={{ mr: 1 }} size="small" color="inherit" onClick={onCancelSelect}>
                  Cancel
                </Button>
              </Box>
            </>
          </ScopeGuard>
        )}
      </Grid>
    </>
  );
}
